// import node module libraries
import { Fragment, useState, useEffect } from 'react';
import { Button, ButtonGroup, Dropdown, DropdownButton, ToggleButton, ToggleButtonGroup, Nav, Navbar, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next'; // useTranslation-Hook
import axios from 'axios';

// Import authentication components
import useAuthHeader from 'react-auth-kit/hooks/useAuthHeader';

// import notification component
import Notification, { handleShowNotification } from 'components/elements/notification/Notification';

const AdminTenantDropdown = (props) => {
	const { t } = useTranslation(); // useTranslation-Hook
	const [notification, setNotification] = useState({ variant: '', title: '', message: '' });
	
	// Tenant Options
	// Select Options
	const [tenantOptions, setTenantOptions] = useState([]);
	// Selected Value
	const [tenant, setTenant] = useState(JSON.parse(localStorage.getItem('_auth_state'))?.tenant_id || '');
	const [tenantName, setTenantName] = useState('');
	// Check if Tenant is selected
	const [isTenantSelected, setIsTenantSelected] = useState(false);
	// Handle Tenant Change
	const handleTenantChange = (tenant, value) => {
		if (tenant === 'resetTenant') {
			setTenant('');
			setTenantName('');
			setIsTenantSelected(false);
			localStorage.removeItem('_auth_state_custom.tenant_name');
			// Reload the page
			window.location.reload();
			return;
		}
		setIsTenantSelected(true);
		setTenant(tenant);
		setTenantName(value.target.textContent);
		
		// Save Tenant in Local Storage
		const authState = JSON.parse(localStorage.getItem('_auth_state')) || {};
		authState.tenant_id = tenant;
		localStorage.setItem('_auth_state', JSON.stringify(authState));
		localStorage.setItem('_auth_state_custom.tenant_name', value.target.textContent);
		
		// Reload the page
		window.location.reload();
	};

	// Role Options
	// Select Options
	const [roleOptions, setRoleOptions] = useState([]);
	// Selected Value
	const [role, setRole] = useState(JSON.parse(localStorage.getItem('_auth_state'))?.role_id || '');
	const [roleName, setRoleName] = useState('');
	// Check if Role is selected
	const [isRoleSelected, setIsRoleSelected] = useState(false);
	// Handle Role Change
	const handleRoleChange = (role, value) => {
		if(role === 'resetRole') {
			setRole('');
			setRoleName('');
			setIsRoleSelected(false);
			localStorage.removeItem('_auth_state_custom.role_name');
			// Reload the page
			window.location.reload();
			return;
		}
		setIsRoleSelected(true);
		setRole(role);
		setRoleName(value.target.textContent);
		
		// Save Role in Local Storage
		const authState = JSON.parse(localStorage.getItem('_auth_state')) || {};
		authState.role_id = role;
		localStorage.setItem('_auth_state', JSON.stringify(authState));
		localStorage.setItem('_auth_state_custom.role_name', value.target.textContent);

		// Reload the page
		window.location.reload();

	};

	// API-Call Options
	const authHeader = useAuthHeader();
	const options = {
		params: {

		},
		headers: {
			Authorization: authHeader,
			Accept: 'application/json'
		}
	};

	// API-Call for Tenant Data
	const getTenantData = async () => {	
		// Dynamische URL basierend auf der Umgebung
		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		// Tenant Data
		await axios.get(`${baseURL}/tenants`, options)
			.then(response => {
				const responseData = response.data.data; // Extract the data from the response
				setTenantOptions(
					responseData.map((tenant) => {
						return {
							eventKey: tenant.id,
							label: tenant.tenant_slug
						};
					})
				);
			})
			.catch((error) => {
				setTenantOptions([]);
				const errorMessage = error.response?.data?.message ? error.message + '. ' + error.response.data.message : error.message;
				handleShowNotification(setNotification, 'danger', 'Fehler', errorMessage + '. Bitte versuche es erneut oder kontaktiere den HelpDesk.');
			});
	};

	// API-Call for Role Data
	const getRoleData = async () => {
		// Dynamische URL basierend auf der Umgebung
		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		// Role Data
		await axios.get(`${baseURL}/roles`, options)
			.then(response => {
				const responseData = response.data.data; // Extract the data from the response
				setRoleOptions(
					responseData.map((role) => {
						return {
							eventKey: role.id,
							label: role.role_name
						};
					})
				);
			})
			.catch((error) => {
				setRoleOptions([]);
				const errorMessage = error.response?.data?.message ? error.message + '. ' + error.response.data.message : error.message;
				handleShowNotification(setNotification, 'danger', 'Fehler', errorMessage + '. Bitte versuche es erneut oder kontaktiere den HelpDesk.');
			});
	};

	useEffect(() => {
		getTenantData();
		getRoleData();
		if (tenantName === '') {
			setTenantName(localStorage.getItem('_auth_state_custom.tenant_name') || '');
		}
		if (roleName === '') {
			setRoleName(localStorage.getItem('_auth_state_custom.role_name') || '');
		}
	}, []);

	return (
		<Fragment>
			{notification.show && (
				<Notification variant={notification.variant} title={notification.title} message={notification.message} show={notification.show} setNotification={setNotification} />
			)}
			<div className="ms-3 navbar-right-wrap d-lg-block">
				<ButtonGroup>
					<DropdownButton align="start" as={ButtonGroup} title={`${t('Tenant')} ${tenantName ? ': ' + tenantName : ': ' + t('Choose')}`} id="bg-nested-dropdown-tenant" variant="outline-secondary" onSelect={handleTenantChange}>
						<Dropdown.Header>{t('CHOOSE TENANT')}</Dropdown.Header>
						{tenantOptions.map((tenantOption) => (
							<Dropdown.Item key={tenantOption.eventKey} id={tenantOption.eventKey} eventKey={tenantOption.eventKey} value={tenantOption.label}>
								{tenantOption.label}
							</Dropdown.Item>
						))}
						<Dropdown.Divider />
						<Dropdown.Item id="resetTenant" eventKey="resetTenant" value="resetTenant">{t('Reset selection')}</Dropdown.Item>
					</DropdownButton>
					<DropdownButton align="end" as={ButtonGroup} title={`${t('Role')} ${roleName ? ': ' + roleName : ': ' + t('Choose')}`} id="bg-nested-dropdown-role" variant="outline-secondary" onSelect={handleRoleChange}>
						<Dropdown.Header>{t('CHOOSE ROLE')}</Dropdown.Header>
						{roleOptions.map((roleOption) => (
							<Dropdown.Item key={roleOption.eventKey} id={roleOption.eventKey} eventKey={roleOption.eventKey} value={roleOption.label}>
								{roleOption.label}
							</Dropdown.Item>
						))}
						<Dropdown.Divider />
						<Dropdown.Item id="resetRole" eventKey="resetRole" value="resetRole">{t('Reset selection')}</Dropdown.Item>
					</DropdownButton>
				</ButtonGroup>
			</div>
		</Fragment>
	);
};

export default AdminTenantDropdown;
